import { useMutation } from '@tanstack/react-query';
import { SsoLogoutResponse, ssoLogoutContract } from '@bliks-io/contracts';
import { apiClient } from '../client';
import { redirectBrowser } from '@/lib/browser-redirect';
import { teardownSessionToLogin } from '@/lib/auth/sessionTeardown';
import { useAuthStore } from '@/stores/auth-store';

/**
 * Logout mutation — owns the ENTIRE logout transition. Callers just
 * `logout.mutate()`; everything else (splash, teardown, navigation or IdP
 * redirect) happens here in an order that never lets the authenticated tree
 * render against a wiped cache:
 * - `onMutate` raises `isLoggingOut` → the full-screen LogoutSplash covers
 *   the app instantly and the global 401/403 handlers go quiet.
 * - The Zitadel end_session URL is resolved while the session cookie is still
 *   valid — local logout alone would leave the IdP session alive and the next
 *   email-first login would silently sign the user back in. The id_token_hint
 *   is read off the server-side session, so the request carries no body.
 * - `clearAuth()` runs BEFORE landing on /login (the reverse auth guard
 *   would bounce an authenticated user straight back into the app).
 * - Normally: full-page redirect through end_session (comes back to /login).
 * - IdP unreachable or no logout URL: navigate to /login, THEN drop the query
 *   cache — local logout still completes.
 *
 * @example
 * ```tsx
 * <MenuItem onClick={() => logout.mutate()}>Log out</MenuItem>
 * ```
 */
export function useLogout() {
  const clearAuth = useAuthStore((state) => state.clearAuth);

  return useMutation({
    mutationFn: async (): Promise<SsoLogoutResponse> => {
      // Always called: this endpoint is the only thing that ends the
      // server-side renewal session and clears its cookie. Skipping it would
      // leave a live refresh token in the database for up to twelve hours
      // after the user believed they logged out. The id_token_hint is read off
      // the server-side session, so the request carries no body.
      //
      // Resolved BEFORE clearing local state so a failing backend cannot
      // strand the user half logged out.
      try {
        const { data } = await apiClient.post<SsoLogoutResponse>(ssoLogoutContract.path, {});
        return data;
      } catch {
        // IdP unreachable — still complete the local logout.
        return { logoutUrl: null };
      }
    },
    onMutate: () => {
      useAuthStore.getState().beginLogout();
    },
    onSuccess: async ({ logoutUrl }) => {
      // Clear auth store (also removes from localStorage)
      clearAuth();

      if (logoutUrl) {
        // Full navigation through Zitadel's end_session — terminates the IdP
        // session and comes back to /login. NO queryClient.clear() here: the
        // cache is in-memory only, the page unload wipes it, and clearing it
        // while the tree is still mounted is exactly what flashed
        // ErrorFallback/"??" before. The splash stays up until unload.
        redirectBrowser(logoutUrl);
        return;
      }

      // No IdP round-trip: shared teardown (land on /login, then drop the cache — see
      // sessionTeardown.ts for the ordering rationale). No router.invalidate()
      // first: re-running the current route's guards after clearAuth just
      // throws a login redirect and pollutes history. The splash must come
      // down even if navigation rejects — a stuck overlay would soft-lock
      // the app.
      try {
        await teardownSessionToLogin();
      } finally {
        useAuthStore.getState().finishLogout();
      }
    }
  });
}
